<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { FILTER_OPERATORS } from '@/utils/selection'

// Cursor-anchored context menu for table cells. Purely presentational — it
// emits intents and lets the host wire them to the query/row-filter apply path
// or to the cross-navigation helpers. Positioning follows ColumnStatsPopover
// but anchors to a point (clientX/clientY) instead of an element rect.

const props = defineProps({
  show: { type: Boolean, default: false },
  x: { type: Number, default: 0 },
  y: { type: Number, default: 0 },
  // Selection items ({ field, value, rawValue, mode, ... }), already distinct.
  items: { type: Array, default: () => [] },
  // Raw selected-cell count (before distinct-by-value collapse). The bulk
  // operators gate on this so selecting many same-valued cells still offers
  // AND/OR/NOT.
  count: { type: Number, default: 0 },
})

const emit = defineEmits([
  'filter-for',
  'filter-out',
  'filter-for-any',
  'apply-bulk',
  'open-selector',
  'open-event',
  'copy',
  'close',
])

const menuRef = ref(null)
const style = ref({})

// Bulk operators are meaningful whenever more than one cell is selected, even
// if they share a value (the duplicates simply collapse in the resulting
// filter). Gate on the raw cell count, falling back to the distinct-item count
// when the host doesn't pass one.
const multiple = computed(() => Math.max(props.count, props.items.length) > 1)
const valueWord = computed(() => (props.items.length > 1 ? 'values' : 'value'))

function updatePosition() {
  if (!menuRef.value) return
  const rect = menuRef.value.getBoundingClientRect()
  const vw = window.innerWidth
  const vh = window.innerHeight
  let left = props.x
  let top = props.y
  if (left + rect.width > vw - 8) left = Math.max(8, vw - rect.width - 8)
  if (top + rect.height > vh - 8) top = Math.max(8, vh - rect.height - 8)
  style.value = { top: `${top}px`, left: `${left}px` }
}

watch(
  () => props.show,
  async (isShown) => {
    if (isShown) {
      style.value = { top: `${props.y}px`, left: `${props.x}px` }
      await nextTick()
      updatePosition()
    }
  },
)

function handleKeydown(event) {
  if (event.key === 'Escape' && props.show) emit('close')
}

function handleClickOutside(event) {
  if (!props.show) return
  if (menuRef.value && !menuRef.value.contains(event.target)) emit('close')
}

// The menu is fixed at the frozen clientX/clientY of the right-click and has no
// anchor element to re-measure, so it can't follow a scroll the way
// CellValueChips does. Both host tables scroll internally, which would leave the
// menu pinned over an unrelated row while still acting on the original cell —
// so close instead. Capture phase: scroll doesn't bubble.
function handleScroll() {
  if (props.show) emit('close')
}

function pick(eventName, ...args) {
  emit(eventName, ...args)
  emit('close')
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
  document.addEventListener('click', handleClickOutside, true)
  document.addEventListener('contextmenu', handleClickOutside, true)
  window.addEventListener('resize', updatePosition)
  window.addEventListener('scroll', handleScroll, true)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('click', handleClickOutside, true)
  document.removeEventListener('contextmenu', handleClickOutside, true)
  window.removeEventListener('resize', updatePosition)
  window.removeEventListener('scroll', handleScroll, true)
})
</script>

<template>
  <Teleport to="body">
    <div v-if="show" ref="menuRef" class="cell-context-menu" :style="style">
      <!-- Filter for / out -->
      <button class="menu-item" @click="pick('filter-for')">
        <font-awesome-icon class="mi-icon" :icon="['fas', 'plus']" />
        <span>Filter for {{ valueWord }}</span>
      </button>
      <button class="menu-item" @click="pick('filter-out')">
        <font-awesome-icon class="mi-icon" :icon="['fas', 'minus']" />
        <span>Filter out {{ valueWord }}</span>
      </button>
      <button class="menu-item" @click="pick('filter-for-any')">
        <font-awesome-icon class="mi-icon" :icon="['fas', 'magnifying-glass']" />
        <span>Filter for {{ valueWord }} (any field)</span>
      </button>

      <!-- Bulk operators (only meaningful with more than one value) -->
      <template v-if="multiple">
        <div class="menu-sep"></div>
        <button class="menu-item" @click="pick('apply-bulk', FILTER_OPERATORS.ALL)">
          <font-awesome-icon class="mi-icon" :icon="['fas', 'layer-group']" />
          <span>Match all (AND)</span>
        </button>
        <button class="menu-item" @click="pick('apply-bulk', FILTER_OPERATORS.ANYOF)">
          <font-awesome-icon class="mi-icon" :icon="['fas', 'circle-nodes']" />
          <span>Match any (OR)</span>
        </button>
        <button class="menu-item" @click="pick('apply-bulk', FILTER_OPERATORS.NOT)">
          <font-awesome-icon class="mi-icon" :icon="['fas', 'ban']" />
          <span>Exclude (NOT)</span>
        </button>
      </template>

      <!-- Cross-navigation -->
      <div class="menu-sep"></div>
      <button class="menu-item" @click="pick('open-selector')">
        <font-awesome-icon class="mi-icon" :icon="['fas', 'diagram-project']" />
        <span>Open in Selector Search</span>
      </button>
      <button class="menu-item" @click="pick('open-event')">
        <font-awesome-icon class="mi-icon" :icon="['fas', 'arrow-up-right-from-square']" />
        <span>Open in Event Search</span>
      </button>

      <!-- Copy -->
      <div class="menu-sep"></div>
      <button class="menu-item" @click="pick('copy')">
        <font-awesome-icon class="mi-icon" :icon="['fas', 'copy']" />
        <span>Copy {{ valueWord }}</span>
      </button>
    </div>
  </Teleport>
</template>

<style scoped>
.cell-context-menu {
  position: fixed;
  z-index: 1000;
  min-width: 15rem;
  padding: 0.25rem;
  background: var(--surface-page);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg, 0 10px 40px rgba(0, 0, 0, 0.4));
  overflow: hidden;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.45rem 0.625rem;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  font-size: 0.8rem;
  text-align: left;
  cursor: pointer;
  transition: background var(--transition-fast, 0.15s) ease;
}

.menu-item:hover {
  background: var(--surface-hover);
}

.menu-item:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.menu-item:disabled:hover {
  background: transparent;
}

.mi-icon {
  width: 0.9rem;
  font-size: 0.75rem;
  color: var(--text-faint);
  flex-shrink: 0;
}

.menu-sep {
  height: 1px;
  margin: 0.25rem 0.375rem;
  background: var(--border-subtle);
}
</style>
